import { useEffect, useMemo, useRef, useState } from 'react';
import { useServiceStore, type PrintKind } from '../../store/serviceStore';
import { useAuthStore } from '../../store/authStore';
import { usePolling } from '../../lib/pollingHook';
import { useTenantTerminology } from '../../lib/useTenantTerminology';
import { useI18n, setLanguage } from '../../lib/i18n';
import { useTheme, setTheme } from '../../lib/theme';
import {
  liveWaitingSeconds,
  formatWaitingTime,
  waitingBand,
  waitingParts,
  type WaitingBand,
} from './helpers';
import { pickName, splitBilingual } from '../kds/helpers';
import type {
  ServiceFilters,
  ServiceDateField,
  ServiceOrder,
  ListServiceOrdersRequest,
} from '../../types/service';

// ─────────────────────────────────────────────────────────────────────────────
// Service Manager — the screen a restaurant manager already knows.
//
// It is a deliberate re-draw of the legacy .NET Kitchen page (pos1/Kitchen):
// the same four filters in the same order, the same DETAILS / ITEMS tabs, the
// same Day:Hour:Min:Sec waiting box, and the same three buttons on the card
// (READY, PRINT, KITCHENPRINT). People have used that page for years and the
// muscle memory is worth more than any layout we might prefer.
//
// What is deliberately NOT the same, because the legacy screen made these hard
// for exactly the people who use it most:
//
//   - One action per row, and it says what it does. The legacy card shows a
//     green READY badge that is also the button, so it reads as a status and
//     acts as a control; people tap it to "see" and bump a dish by accident.
//     Here an unfinished line has a button that says Ready, and a finished one
//     is a struck-through row with a tick. No control ever doubles as a label.
//   - Bumping the WHOLE order asks twice. It cannot be undone from this screen
//     and it clears the card off the board.
//   - Printing answers. Every print reports where the paper went, or why it
//     did not go anywhere — see the toasts at the bottom.
//   - Arabic. The item names were always Arabic; now the chrome can be too.
// ─────────────────────────────────────────────────────────────────────────────

export interface ServiceScreenProps {
  /** Switch back to the Kitchen Display. */
  onOpenKds: () => void;
}

export function ServiceScreen({ onOpenKds }: ServiceScreenProps): JSX.Element {
  const orders = useServiceStore((s) => s.orders);
  const serverTime = useServiceStore((s) => s.serverTime);
  const status = useServiceStore((s) => s.status);
  const error = useServiceStore((s) => s.error);
  const filters = useServiceStore((s) => s.filters);
  const filterOptions = useServiceStore((s) => s.filterOptions);
  const search = useServiceStore((s) => s.search);
  const lateOnly = useServiceStore((s) => s.lateOnly);
  const toasts = useServiceStore((s) => s.toasts);
  const printing = useServiceStore((s) => s.printing);
  const setFilters = useServiceStore((s) => s.setFilters);
  const setSearch = useServiceStore((s) => s.setSearch);
  const setLateOnly = useServiceStore((s) => s.setLateOnly);
  const resetFilters = useServiceStore((s) => s.resetFilters);
  const refresh = useServiceStore((s) => s.refresh);
  const loadFilterOptions = useServiceStore((s) => s.loadFilterOptions);
  const markLineReady = useServiceStore((s) => s.markLineReady);
  const markOrderReady = useServiceStore((s) => s.markOrderReady);
  const print = useServiceStore((s) => s.print);
  const dismissToast = useServiceStore((s) => s.dismissToast);

  const { lang, dir, t } = useI18n();
  const theme = useTheme();
  const authState = useAuthStore((s) => s.state);
  const logout = useAuthStore((s) => s.logout);

  const applyFilters = (patch: Partial<ListServiceOrdersRequest>): void => {
    setFilters(patch);
    void refresh();
  };

  useEffect(() => {
    if (!filterOptions) void loadFilterOptions();
  }, [filterOptions, loadFilterOptions]);

  usePolling(refresh, 5000, true);

  // One tick a second drives every card's timer.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Elapsed time is computed once for the whole board so every card agrees, and
  // so the late/search filters below read the same numbers the cards show.
  //
  // `tick` is a real dependency, not decoration: without it the memo only
  // recomputes when a poll lands and the timers move in five-second jumps.
  const withElapsed = useMemo(
    () =>
      orders.map((order) => ({
        order,
        elapsed: liveWaitingSeconds(serverTime, order.waitingSeconds),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, serverTime, tick],
  );

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return withElapsed.filter(({ order, elapsed }) => {
      if (lateOnly && waitingBand(elapsed) !== 'late') return false;
      if (!needle) return true;
      return orderMatches(order, needle);
    });
  }, [withElapsed, search, lateOnly]);

  const lateCount = withElapsed.filter((o) => waitingBand(o.elapsed) === 'late').length;

  // Belt-and-braces guard: if somehow we render this screen outside the
  // 'session' state (shouldn't happen — App.tsx routes by state.kind), bail.
  if (authState.kind !== 'session') {
    return <div className="svc-empty">{t('loadingSession')}</div>;
  }

  const session = authState.data.session;

  return (
    <div className="svc-root" dir={dir} lang={lang}>
      <header className="svc-top">
        <div className="svc-top-left">
          <h1 className="svc-title">{t('serviceManager')}</h1>
          <bdi className="svc-sub">
            {session.subsidiary_name} · {session.branch_name} · {session.username}
          </bdi>
        </div>

        <div className="svc-top-stats">
          <Stat label={t('orders')} value={visible.length} />
          <Stat label={t('late')} value={lateCount} tone={lateCount > 0 ? 'late' : undefined} />
        </div>

        <div className="svc-top-right">
          <button
            type="button"
            className="svc-btn svc-btn-ghost"
            onClick={() => setLanguage(lang === 'ar' ? 'en' : 'ar')}
            title={lang === 'ar' ? 'English' : 'العربية'}
          >
            {lang === 'ar' ? 'EN' : 'ع'}
          </button>
          <button
            type="button"
            className="svc-btn svc-btn-ghost"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? t('lightMode') : t('darkMode')}
            aria-label={theme === 'dark' ? t('lightMode') : t('darkMode')}
          >
            {theme === 'dark' ? '☀' : '☾'}
          </button>
          <button type="button" className="svc-btn svc-btn-primary" onClick={onOpenKds}>
            {t('kds')}
          </button>
          <button type="button" className="svc-btn svc-btn-ghost" onClick={() => logout()}>
            {t('signOut')}
          </button>
        </div>
      </header>

      <FilterBar
        filters={filters}
        options={filterOptions}
        loading={status === 'loading'}
        search={search}
        lateOnly={lateOnly}
        onChange={applyFilters}
        onSearch={setSearch}
        onLateOnly={setLateOnly}
        onReset={resetFilters}
        onRefresh={() => void refresh()}
      />

      {error && <div className="svc-error">⚠ {error}</div>}

      {status === 'loading' && orders.length === 0 && (
        <div className="svc-empty">{t('loadingQueue')}</div>
      )}

      {status !== 'loading' && visible.length === 0 && (
        <div className="svc-empty">{t('noOrders')}</div>
      )}

      <div className="svc-board">
        {visible.map(({ order, elapsed }) => (
          <OrderCard
            key={order.id}
            order={order}
            elapsedSeconds={elapsed}
            band={waitingBand(elapsed)}
            printing={printing}
            onMarkLine={(lineId) => void markLineReady(lineId)}
            onMarkOrder={() => void markOrderReady(order.id)}
            onPrint={(kind) => void print(order.id, kind)}
          />
        ))}
      </div>

      <div className="svc-toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <button
            key={toast.id}
            type="button"
            className={`svc-toast ${toast.tone}`}
            onClick={() => dismissToast(toast.id)}
          >
            <span className="svc-toast-icon">{toast.tone === 'ok' ? '🖨' : '⚠'}</span>
            <span>
              <b>{toast.tone === 'ok' ? t('sent') : t('printFailed')}</b>
              <em>{toast.message}</em>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Order-level free-text match: number, table, customer, branch, item name. */
function orderMatches(order: ServiceOrder, needle: string): boolean {
  if (String(order.orderNumber).includes(needle)) return true;
  if (order.tableNumber !== null && String(order.tableNumber) === needle) return true;
  const haystacks = [
    order.customerName,
    order.customerNameAr,
    order.branchName,
    order.branchNameAr,
    order.orderTypeLabel,
  ];
  if (haystacks.some((h) => h && h.toLowerCase().includes(needle))) return true;
  return order.lines.some(
    (l) =>
      l.itemName.toLowerCase().includes(needle) ||
      l.itemNameAr.toLowerCase().includes(needle),
  );
}

function Stat(props: { label: string; value: number; tone?: 'late' }): JSX.Element {
  return (
    <span className={`svc-stat ${props.tone ?? ''}`}>
      <b>{props.value}</b>
      <em>{props.label}</em>
    </span>
  );
}

// ── Filter bar ────────────────────────────────────────────────────────────

interface FilterBarProps {
  filters: ListServiceOrdersRequest;
  options: ServiceFilters | null;
  loading: boolean;
  search: string;
  lateOnly: boolean;
  onChange: (patch: Partial<ListServiceOrdersRequest>) => void;
  onSearch: (value: string) => void;
  onLateOnly: (value: boolean) => void;
  onReset: () => void;
  onRefresh: () => void;
}

function FilterBar({
  filters,
  options,
  loading,
  search,
  lateOnly,
  onChange,
  onSearch,
  onLateOnly,
  onReset,
  onRefresh,
}: FilterBarProps): JSX.Element {
  const { lang, t } = useI18n();
  // Collapsed by default on a phone-sized screen: the filters are set once a
  // shift and the orders are what matter. Never collapsed on a big screen.
  const [open, setOpen] = useState(true);

  const branches = options?.branches ?? [];
  const orderTypes = options?.orderTypes ?? [];
  const categories = options?.categories ?? [];
  const localized = <T extends { name: string; nameAr: string }>(row: T): string =>
    lang === 'ar' ? row.nameAr || row.name : row.name;

  // Branch: single-select that maps to `branchIds: [id]` or undefined for "All".
  const branchSelectValue =
    filters.branchIds && filters.branchIds.length === 1 ? String(filters.branchIds[0]) : '';
  const handleBranchChange = (v: string): void => {
    if (v === '') onChange({ branchIds: undefined });
    else onChange({ branchIds: [Number(v)] });
  };

  // Order type: '' = all, 'dine-in' = special value, else numeric type id.
  const orderTypeSelectValue =
    filters.orderType === undefined || filters.orderType === 'all'
      ? ''
      : String(filters.orderType);
  const handleOrderTypeChange = (v: string): void => {
    if (v === '') onChange({ orderType: 'all' });
    else if (v === 'dine-in') onChange({ orderType: 'dine-in' });
    else onChange({ orderType: Number(v) });
  };

  // Date: one day, bound to both ends of the range (as the legacy screen does).
  const handleDateChange = (v: string): void => {
    if (!v) return;
    onChange({ startDate: v, endDate: v });
  };
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate(),
  ).padStart(2, '0')}`;

  const handleDateFieldChange = (next: ServiceDateField): void => {
    if (filters.dateField === next) return;
    onChange({ dateField: next });
  };

  const activeCount =
    (filters.branchIds?.length ? 1 : 0) +
    (filters.orderType !== undefined && filters.orderType !== 'all' ? 1 : 0) +
    (filters.categoryIds?.length ? 1 : 0) +
    (filters.startDate !== todayStr ? 1 : 0);

  return (
    <section className={`svc-filters ${open ? 'open' : ''}`}>
      <div className="svc-filters-head">
        <button
          type="button"
          className="svc-filters-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className="svc-filters-icon" aria-hidden="true">
            ⚙
          </span>
          {t('filters')}
          {activeCount > 0 && <span className="svc-filters-badge">{activeCount}</span>}
        </button>

        {/* Search and Refresh stay visible even when the filters are folded —
            they are the two controls used every few minutes. */}
        <label className="svc-search">
          <span aria-hidden="true">🔍</span>
          <input
            type="search"
            value={search}
            placeholder={t('search')}
            onChange={(e) => onSearch(e.target.value)}
          />
        </label>

        <button
          type="button"
          className={`svc-chip ${lateOnly ? 'active' : ''}`}
          onClick={() => onLateOnly(!lateOnly)}
          aria-pressed={lateOnly}
        >
          ⏱ {t('lateOnly')}
        </button>

        <button
          type="button"
          className={`svc-btn svc-btn-primary ${loading ? 'spinning' : ''}`}
          onClick={onRefresh}
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M3 12a9 9 0 0 1 15.5-6.4L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-15.5 6.4L3 16" />
            <path d="M3 21v-5h5" />
          </svg>
          {t('refresh')}
        </button>
      </div>

      {open && (
        <div className="svc-filters-body">
          <div className="svc-field">
            <label className="svc-label" htmlFor="svc-branch">
              {t('branch')}
            </label>
            <select
              id="svc-branch"
              className="svc-select"
              value={branchSelectValue}
              onChange={(e) => handleBranchChange(e.target.value)}
            >
              <option value="">{t('allBranches')}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {localized(b)}
                </option>
              ))}
            </select>
          </div>

          <div className="svc-field">
            <label className="svc-label" htmlFor="svc-ordertype">
              {t('orderType')}
            </label>
            <select
              id="svc-ordertype"
              className="svc-select"
              value={orderTypeSelectValue}
              onChange={(e) => handleOrderTypeChange(e.target.value)}
            >
              <option value="">{t('allTypes')}</option>
              {orderTypes.map((ot) => (
                <option key={String(ot.value)} value={String(ot.value)}>
                  {lang === 'ar' ? ot.labelAr || ot.label : ot.label}
                </option>
              ))}
            </select>
          </div>

          <div className="svc-field">
            <label className="svc-label" htmlFor="svc-date">
              {t('date')}
            </label>
            <div className="svc-date-row">
              <input
                id="svc-date"
                type="date"
                className="svc-date"
                value={filters.startDate}
                onChange={(e) => handleDateChange(e.target.value)}
              />
              <button
                type="button"
                className="svc-chip"
                onClick={() => handleDateChange(todayStr)}
                disabled={filters.startDate === todayStr}
              >
                {t('today')}
              </button>
            </div>
          </div>

          <div className="svc-field">
            <label className="svc-label">{t('categories')}</label>
            <CategoriesMultiSelect
              all={categories.map((c) => ({ id: c.id, name: localized(c) }))}
              selected={filters.categoryIds ?? []}
              onChange={(next) => onChange({ categoryIds: next.length > 0 ? next : undefined })}
            />
          </div>

          <div className="svc-field svc-field-wide">
            <label className="svc-label">{t('dateBasis')}</label>
            <div className="svc-segment">
              <button
                type="button"
                className={filters.dateField === 'order' ? 'active' : ''}
                onClick={() => handleDateFieldChange('order')}
              >
                {t('orderDate')}
              </button>
              <button
                type="button"
                className={filters.dateField === 'delivery' ? 'active' : ''}
                onClick={() => handleDateFieldChange('delivery')}
              >
                {t('deliveryDate')}
              </button>
            </div>
          </div>

          <div className="svc-field svc-field-reset">
            <button type="button" className="svc-btn svc-btn-ghost" onClick={onReset}>
              ↺ {t('resetFilters')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Categories multi-select ───────────────────────────────────────────────

interface CategoriesMultiSelectProps {
  all: { id: number; name: string }[];
  selected: number[];
  onChange: (next: number[]) => void;
}

function CategoriesMultiSelect({
  all,
  selected,
  onChange,
}: CategoriesMultiSelectProps): JSX.Element {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const selectedSet = new Set(selected);
  const placeholder = selected.length === 0;
  const summary = placeholder
    ? t('allCategories')
    : selected.length === all.length
      ? t('allCategories')
      : `${selected.length} ${t('selected')}`;

  const toggleOne = (id: number): void => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  return (
    <div className="svc-multiselect" ref={wrapRef}>
      <button
        type="button"
        className={`svc-multiselect-trigger ${placeholder ? 'placeholder' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="summary">{summary}</span>
        <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="svc-multiselect-panel" role="listbox">
          <div className="svc-multiselect-actions">
            <button type="button" onClick={() => onChange(all.map((c) => c.id))}>
              {t('selectAll')}
            </button>
            <button type="button" onClick={() => onChange([])}>
              {t('clear')}
            </button>
          </div>
          {all.map((c) => (
            <label key={c.id} className="svc-multiselect-option">
              <input
                type="checkbox"
                checked={selectedSet.has(c.id)}
                onChange={() => toggleOne(c.id)}
              />
              <span>{c.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Order card ────────────────────────────────────────────────────────────

interface OrderCardProps {
  order: ServiceOrder;
  elapsedSeconds: number;
  band: WaitingBand;
  printing: Set<string>;
  onMarkLine: (lineId: number) => void;
  onMarkOrder: () => void;
  onPrint: (kind: PrintKind) => void;
}

function OrderCard({
  order,
  elapsedSeconds,
  band,
  printing,
  onMarkLine,
  onMarkOrder,
  onPrint,
}: OrderCardProps): JSX.Element {
  const { lang, t } = useI18n();
  const term = useTenantTerminology();
  const [tab, setTab] = useState<'items' | 'details'>('items');
  // The whole-order bump is irreversible from this screen, so it arms first.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const id = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(id);
  }, [armed]);

  const readyCount = order.lines.filter((l) => l.isReady).length;
  const parts = waitingParts(elapsedSeconds);
  const name = (line: { itemName: string; itemNameAr: string }): string =>
    lang === 'ar' ? line.itemNameAr || line.itemName : line.itemName;

  return (
    <article className={`svc-card band-${band}`}>
      <header className="svc-card-head">
        <div className="svc-card-head-main">
          <span className="svc-pill type">{order.orderTypeLabel}</span>
          <span className="svc-pill count">{order.lines.length}</span>
          <bdi className="svc-card-branch">
            {lang === 'ar' ? order.branchNameAr || order.branchName : order.branchName}
          </bdi>
        </div>
        <span className="svc-card-no">#{order.orderNumber}</span>
      </header>

      <div className="svc-progress" aria-hidden="true">
        <i style={{ width: `${(readyCount / Math.max(1, order.lines.length)) * 100}%` }} />
      </div>

      <div className="svc-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'items'}
          className={tab === 'items' ? 'active' : ''}
          onClick={() => setTab('items')}
        >
          {t('items')}
          <span className="svc-tab-note">
            {readyCount}/{order.lines.length} {t('ofReady')}
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'details'}
          className={tab === 'details' ? 'active' : ''}
          onClick={() => setTab('details')}
        >
          {t('details')}
        </button>
      </div>

      {tab === 'items' ? (
        <ul className="svc-lines">
          {order.lines.length === 0 && <li className="svc-line-empty">{t('noLines')}</li>}
          {order.lines.map((line) => (
            <li key={line.id} className={`svc-line ${line.isReady ? 'done' : ''}`}>
              <span className="svc-qty">{line.qty}</span>
              <div className="svc-line-main">
                <bdi className="svc-line-name">{name(line)}</bdi>
                {((line.modifiers ?? []).length > 0 ||
                  (line.components ?? []).length > 0) && (
                  <div className="svc-mods">
                    {(line.modifiers ?? []).map((m, i) => {
                      // Same rule the KDS card uses: prefer the two halves the
                      // API now sends, and fall back to splitting the merged
                      // ERP title so an option stops reading "ملفوف Malfof".
                      const n = m.nameAr ? { en: m.name, ar: m.nameAr } : splitBilingual(m.name);
                      return (
                        <span key={`${line.id}-${i}`} className="svc-mod">
                          <bdi>{pickName(lang, n.en, n.ar)}</bdi>
                        </span>
                      );
                    })}
                    {/*
                      The contents of a finished product ("Mahashi Mix" → onion,
                      kosa, vine leaves …), after the chosen options and in the
                      same chip — the KDS card renders them exactly this way.
                      Names only, in the screen's language: the item master
                      carries both scripts, so there is no title to split.
                    */}
                    {(line.components ?? []).map((c, i) => (
                      <span key={`${line.id}-comp-${i}`} className="svc-mod">
                        <bdi>{pickName(lang, c.name, c.nameAr ?? null)}</bdi>
                      </span>
                    ))}
                  </div>
                )}
                {line.notes && <div className="svc-line-note">“{line.notes}”</div>}
              </div>
              {line.isReady ? (
                <span className="svc-line-done">✓ {t('ready')}</span>
              ) : (
                <button
                  type="button"
                  className="svc-line-btn"
                  onClick={() => onMarkLine(line.id)}
                >
                  {t('markReady')}
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <dl className="svc-details">
          <Detail label={t('orderNo')} value={`#${order.orderNumber}`} />
          <Detail label={t('orderType')} value={order.orderTypeLabel} />
          <Detail
            label={t('branch')}
            value={lang === 'ar' ? order.branchNameAr || order.branchName : order.branchName}
          />
          {order.tableNumber !== null && (
            <Detail
              label={lang === 'ar' ? term.table.ar : term.table.en}
              value={String(order.tableNumber)}
            />
          )}
          <Detail
            label={t('customer')}
            value={
              (lang === 'ar' ? order.customerNameAr || order.customerName : order.customerName) ??
              '—'
            }
          />
          <Detail label={t('placed')} value={`${order.orderDate} ${order.orderTime}`} />
          {order.deliveryDate && (
            <Detail label={t('deliveryDate')} value={order.deliveryDate.slice(0, 16).replace('T', ' ')} />
          )}
        </dl>
      )}

      <div className="svc-wait">
        <span className="svc-wait-title">{t('waitingTime')}</span>
        <div className="svc-wait-boxes">
          <WaitBox label={t('days')} value={parts.days} />
          <WaitBox label={t('hours')} value={parts.hours} />
          <WaitBox label={t('minutes')} value={parts.minutes} />
          <WaitBox label={t('seconds')} value={parts.seconds} />
        </div>
        <span className="svc-wait-compact">{formatWaitingTime(elapsedSeconds)}</span>
      </div>

      <footer className="svc-card-foot">
        <button
          type="button"
          className={`svc-foot-btn ready ${armed ? 'armed' : ''}`}
          onClick={() => {
            if (armed) onMarkOrder();
            else setArmed(true);
          }}
        >
          {armed ? t('confirmReady') : `✓ ${t('allReady')}`}
        </button>
        <PrintButton
          kind="receipt"
          label={t('print')}
          title={t('printReceipt')}
          busy={printing.has(`${order.id}:receipt`)}
          onClick={() => onPrint('receipt')}
        />
        <PrintButton
          kind="kitchen"
          label={t('kitchenPrint')}
          title={t('printKitchen')}
          busy={printing.has(`${order.id}:kitchen`)}
          onClick={() => onPrint('kitchen')}
        />
      </footer>
    </article>
  );
}

function Detail(props: { label: string; value: string }): JSX.Element {
  return (
    <div className="svc-detail">
      <dt>{props.label}</dt>
      <dd>
        <bdi>{props.value}</bdi>
      </dd>
    </div>
  );
}

function WaitBox(props: { label: string; value: number }): JSX.Element {
  return (
    <span className="svc-wait-box">
      <b>{String(props.value).padStart(2, '0')}</b>
      <em>{props.label}</em>
    </span>
  );
}

function PrintButton(props: {
  kind: PrintKind;
  label: string;
  title: string;
  busy: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`svc-foot-btn ${props.kind} ${props.busy ? 'busy' : ''}`}
      onClick={props.onClick}
      disabled={props.busy}
      title={props.title}
    >
      {props.busy ? '…' : `🖨 ${props.label}`}
    </button>
  );
}

